import React from "react";
import { Link } from "react-router-dom";
import { Shield, Database, Lock, UserCheck, Baby, FileText, Mail, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { useReveal } from "../lib/motion";

const Section = ({ id, icon: Icon, eyebrow, title, children }) => (
    <section id={id} className="scroll-mt-24" data-testid={`privacy-section-${id}`}>
        <div className="flex items-center gap-3 mb-3">
            <span className="w-9 h-9 rounded-full bg-femcare-petal flex items-center justify-center">
                <Icon className="w-4 h-4 text-femcare-pink" />
            </span>
            <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent font-medium">{eyebrow}</p>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-femcare-purple mb-6 leading-tight">{title}</h2>
        <div className="space-y-4 text-femcare-purple/80 leading-relaxed">{children}</div>
    </section>
);

const Bullet = ({ label, children }) => (
    <li className="flex gap-3">
        <span className="text-femcare-pink mt-1.5 shrink-0">•</span>
        <p className="text-femcare-purple/80">
            {label && <strong className="text-femcare-purple font-semibold">{label}</strong>}
            {children}
        </p>
    </li>
);

export default function Privacy() {
    useReveal();
    return (
        <div className="bg-femcare-cream" data-testid="privacy-page">
            <PageHero
                testId="privacy-hero"
                overline="Privacy &amp; Terms"
                title={<>Privacy Policy<br /><span className="italic font-light">&amp; Terms.</span></>}
                subtitle={`Femcare ("we", "us", "our") is a women's health companion. This page summarises how we handle your data, why we collect it, and how we keep it safe.`}
                size="lg"
            >
                <p className="text-sm text-femcare-accent">Last updated: April 27, 2026</p>
            </PageHero>

            <div className="max-w-5xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24 pt-8">
                {/* Sticky TOC */}
                <aside className="lg:col-span-3 hidden lg:block">
                    <nav className="sticky top-24 space-y-2 text-sm" data-testid="privacy-toc">
                        <p className="text-xs uppercase tracking-[0.25em] text-femcare-accent mb-3">On this page</p>
                        {[
                            ["data-safety", "Data Safety"],
                            ["what-we-collect", "What we collect"],
                            ["why-we-collect", "Why we collect it"],
                            ["how-we-protect", "How we protect it"],
                            ["your-rights", "Your rights"],
                            ["children", "Children's privacy"],
                            ["terms", "Terms of Use"],
                            ["contact", "Contact"],
                        ].map(([id, label]) => (
                            <a
                                key={id}
                                href={`#${id}`}
                                className="block py-1.5 text-femcare-purple/65 hover:text-femcare-pink border-l-2 border-transparent hover:border-femcare-pink pl-3 transition"
                            >
                                {label}
                            </a>
                        ))}
                    </nav>
                </aside>

                {/* Body */}
                <article className="lg:col-span-9 space-y-16">
                    {/* DATA SAFETY OVERVIEW CARD */}
                    <Section id="data-safety" icon={Shield} eyebrow="Overview" title="Data Safety">
                        <p className="text-femcare-purple/70">
                            What we collect · why · how we protect it.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                            {[
                                { Icon: Database, label: "What we collect", anchor: "what-we-collect" },
                                { Icon: UserCheck, label: "Why we collect it", anchor: "why-we-collect" },
                                { Icon: Lock, label: "How we protect it", anchor: "how-we-protect" },
                            ].map(({ Icon, label, anchor }) => (
                                <a
                                    key={anchor}
                                    href={`#${anchor}`}
                                    className="card-editorial p-5 group reveal"
                                >
                                    <Icon className="w-5 h-5 text-femcare-pink mb-3" />
                                    <p className="font-serif text-lg text-femcare-purple flex items-center gap-1.5">
                                        {label}
                                        <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition" />
                                    </p>
                                </a>
                            ))}
                        </div>
                    </Section>

                    <Section id="what-we-collect" icon={Database} eyebrow="01" title="What data we collect">
                        <ul className="space-y-3">
                            <Bullet label="Account info — ">email, name, password (stored hashed), date of birth, age, city, country.</Bullet>
                            <Bullet label="Profile & cycle data — ">last period date, cycle length, period length, marital status (India only), tracking status (Cycle / TTC / Pregnancy), profile photo (optional).</Bullet>
                            <Bullet label="Daily logs — ">flow, mood, symptoms, basal body temperature, cervical mucus, OPK results, intercourse status, weight, kick count, water, sleep, free-text notes.</Bullet>
                            <Bullet label="Pregnancy data — ">LMP, due date, week-by-week milestones you log.</Bullet>
                            <Bullet label="Community content — ">posts and comments you create, optionally with images.</Bullet>
                            <Bullet label="AI chat history — ">messages you send to the Femcare Care Assistant for the duration of the chat session.</Bullet>
                            <Bullet label="Device permissions — ">push-notification token (only if you opt in), and camera / photo-library access (only when you choose to upload a profile photo or community image).</Bullet>
                        </ul>
                        <div className="mt-6 bg-femcare-petal/40 border border-femcare-border rounded-2xl p-5">
                            <p className="text-xs uppercase tracking-[0.25em] text-femcare-accent mb-2 font-medium">What we do NOT collect</p>
                            <p className="text-femcare-purple/85">
                                We do <strong>not</strong> collect precise location, contacts, microphone audio, SMS, call logs, or any
                                data unrelated to women's wellness.
                            </p>
                        </div>
                    </Section>

                    <Section id="why-we-collect" icon={UserCheck} eyebrow="02" title="Why we collect it">
                        <ul className="space-y-3">
                            <Bullet label="Personalised tracking — ">to predict your next period, fertile window, and ovulation accurately, and to compute your weekly pregnancy stage.</Bullet>
                            <Bullet label="Personalised insights & notifications — ">to send age- and phase-appropriate tips (e.g. "Period in 2 days" reminders, week-by-week pregnancy snapshots).</Bullet>
                            <Bullet label="AI Care Assistant — ">to give you tailored, evidence-based answers about your cycle, fertility, or pregnancy stage. Only the minimum context needed for an answer is sent to the AI provider.</Bullet>
                            <Bullet label="Community — ">to host posts/comments you choose to publish and let other Femcare users engage with them.</Bullet>
                            <Bullet label="Account & security — ">to authenticate you, secure your session, and recover your account.</Bullet>
                            <Bullet label="App improvement — ">anonymous, aggregated usage signals to improve features. <strong>The free version shows ads served by Google. We never sell your health data, and ads are not targeted using your cycle or health information. Premium is ad-free.</strong></Bullet>
                        </ul>
                    </Section>

                    <Section id="how-we-protect" icon={Lock} eyebrow="03" title="How we protect it">
                        <ul className="space-y-3">
                            <Bullet label="Encryption in transit — ">every request between the app and our servers is sent over HTTPS / TLS 1.2+.</Bullet>
                            <Bullet label="Password security — ">passwords are hashed with bcrypt before storage. We never store or log your plain password.</Bullet>
                            <Bullet label="Authentication — ">sessions are managed using short-lived JWT access tokens and rotating refresh tokens.</Bullet>
                            <Bullet label="Database access — ">your data is stored in a managed MongoDB cluster with restricted network access; only authorised application services can read/write it.</Bullet>
                            <Bullet label="Email verification — ">sign-up uses a one-time-passcode (OTP) over email to confirm ownership before any data is created.</Bullet>
                            <Bullet label="Data minimisation — ">we only collect what is needed for the features you use; if you turn a tracker off, that data is no longer recorded.</Bullet>
                            <Bullet label="No third-party sale — ">we never sell, rent, or trade your personal data.</Bullet>
                            <Bullet label="Your control — ">you can delete any post, log entry, or your entire account at any time. Account deletion permanently removes your personal data within 30 days.</Bullet>
                        </ul>
                    </Section>

                    <Section id="your-rights" icon={UserCheck} eyebrow="04" title="Your rights">
                        <ul className="space-y-3">
                            <Bullet>Access, export, or delete your data on request.</Bullet>
                            <Bullet>Withdraw consent and disable tracking modes (Cycle / TTC / Pregnancy) any time from Profile.</Bullet>
                            <Bullet>Opt out of push notifications from Profile → Notifications.</Bullet>
                            <Bullet>
                                Reach us at{" "}
                                <a href="mailto:support@femcare.life" className="text-femcare-pink underline decoration-femcare-pink/30 underline-offset-4 hover:decoration-femcare-pink">
                                    support@femcare.life
                                </a>{" "}
                                for any privacy request — we respond within 30 days.
                            </Bullet>
                        </ul>
                    </Section>

                    <Section id="children" icon={Baby} eyebrow="05" title="Children's privacy">
                        <p>
                            Femcare is intended for users aged <strong>13 and above</strong>. Users under 18 in India are limited to
                            cycle-tracking features only; Trying to Conceive and Pregnancy modes are disabled by default for minors.
                        </p>
                        <p>
                            Parents or guardians who believe their child has provided personal data without consent may contact us to
                            have it removed.
                        </p>
                    </Section>

                    <Section id="terms" icon={FileText} eyebrow="06" title="Terms of Use (summary)">
                        <ul className="space-y-3">
                            <Bullet>Femcare provides general wellness insights and is <strong>not intended for medical diagnosis or treatment.</strong></Bullet>
                            <Bullet>Always consult a qualified healthcare professional for medical advice.</Bullet>
                            <Bullet>Be respectful in the Community. No spam, harassment, or hateful content.</Bullet>
                            <Bullet>You retain ownership of your content; you grant Femcare a non-exclusive licence to display it within the app.</Bullet>
                            <Bullet>We may update these Terms — material changes will be announced in-app and by email.</Bullet>
                        </ul>
                    </Section>

                    <Section id="contact" icon={Mail} eyebrow="07" title="Contact">
                        <p>
                            Questions, requests, or concerns? Email us at{" "}
                            <a href="mailto:support@femcare.life" className="text-femcare-pink underline decoration-femcare-pink/30 underline-offset-4 hover:decoration-femcare-pink font-medium" data-testid="privacy-contact-email">
                                support@femcare.life
                            </a>
                            .
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/contact" className="inline-flex items-center gap-2 bg-femcare-pink hover:bg-femcare-rose text-white rounded-full px-6 py-3 transition text-sm font-medium" data-testid="privacy-contact-cta">
                                <Mail className="w-4 h-4" /> Contact form
                                <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                            <Link to="/" className="inline-flex items-center gap-2 border border-femcare-border hover:border-femcare-pink text-femcare-purple rounded-full px-6 py-3 transition text-sm font-medium">
                                Back to home
                            </Link>
                        </div>
                    </Section>

                    <p className="text-xs text-femcare-purple/40 pt-8 border-t border-femcare-border">
                        © 2026 Femcare. All rights reserved.
                    </p>
                </article>
            </div>
        </div>
    );
}
